import React, { useMemo } from "react";
import styled from "styled-components";
import { AmplitudeService } from "@analytics/Amplitude/AmplitudeService";
import { LifeAtButtonV2 } from "@common/LifeAtButtonV2/LifeAtButtonV2";
import { FontSize } from "@themes/Fonts";
import { projectList$, selectedProjectId$ } from "../../projects/ProjectGlobals";
import CreateProjectButton from "../../projects/components/CreateProjectButton";
import { FilterTasksByProjectEvent } from "../AmplitudeTaskFilterEvents";
import { useTaskListContext } from "../TaskListContext";
import { mainContent$ } from "../planner/PlannerGlobals";
import ProjectChip from "./ProjectChip";
import {
    FilterCheckbox,
    FilterItem,
    FilterItemsList,
    FilteringContainer,
    SectionHeader,
} from "./TaskTagFilterSidebarList";

const PROJECTS_HEADER = "Projects";
const OPEN_PROJECT_DETAILS_LABEL = "Open";
const ALL_OPTION_LABEL = "All";

const OpenProjectButton = styled(LifeAtButtonV2)`
    margin-left: auto;
    visibility: hidden;
`;

const StyledProjectChip = styled(ProjectChip)`
    padding: unset;
`;

const ProjectListItem = styled(FilterItem)`
    padding: 4px 6px;

    &:hover ${OpenProjectButton} {
        visibility: visible;
    }
`;

export function ProjectsSidebarList() {
    const amplitudeService = AmplitudeService.getInstance();
    const { filters, toggleProjectFilter, clearProjectFilters } = useTaskListContext();

    const selectedProjectIdSet = useMemo(() => {
        const ret = new Set<string>();
        for (const filter of filters) {
            if (filter.type === "project") {
                ret.add(filter.project);
            }
        }
        return ret;
    }, [filters]);

    return (
        <FilteringContainer>
            <SectionHeader>{PROJECTS_HEADER}</SectionHeader>
            <FilterItemsList>
                <ProjectListItem
                    onClick={() => {
                        clearProjectFilters();
                    }}
                >
                    <FilterCheckbox checked={selectedProjectIdSet.size === 0} />
                    {ALL_OPTION_LABEL}
                </ProjectListItem>
                {Object.entries(projectList$.value).map(([projectId, project]) => (
                    <ProjectListItem
                        key={projectId}
                        onClick={() => {
                            toggleProjectFilter(projectId);
                            amplitudeService.logEvent(
                                new FilterTasksByProjectEvent({
                                    source: "board-view",
                                    selectedProjectsCount: selectedProjectIdSet.size,
                                }),
                            );
                        }}
                    >
                        <FilterCheckbox
                            checked={selectedProjectIdSet.has(projectId)}
                            color={project.color ?? undefined}
                        />
                        <StyledProjectChip project={project} fontSizeCss={FontSize.normal} />
                        <OpenProjectButton
                            onClick={() => {
                                selectedProjectId$.value = projectId;
                                mainContent$.value = "projects";
                            }}
                            variant={"solid"}
                            size={"small"}
                            disableHover={true}
                        >
                            {OPEN_PROJECT_DETAILS_LABEL}
                        </OpenProjectButton>
                    </ProjectListItem>
                ))}
            </FilterItemsList>
            <CreateProjectButton amplitudeSource={"planner-sidebar"} />
        </FilteringContainer>
    );
}
